/**
 * The Prompt Analyzer: pick a session and see why it went as it did — where the time went, how long
 * nothing happened, what failed, what blocked it, how much input was context sent again — and ask
 * anything about it. We have the data.
 *
 * The findings are computed by DevPulse for everybody; a question in your own words goes, with the
 * facts, to your own agent (plans with analysis).
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { HelpCircle, Loader2, Send } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { useOrderResult } from '../lib/useOrderResult'
import { ErrorBanner, Skeleton } from './primitives'

const duration = (seconds: number) =>
  seconds >= 3600 ? `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}min` : seconds >= 60 ? `${Math.floor(seconds / 60)}min` : `${seconds}s`

export function PromptAnalyzer() {
  const { t, locale } = useI18n()
  const [sessionId, setSessionId] = useState('')
  const [question, setQuestion] = useState('')
  const [orderId, setOrderId] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const { data: sessions } = useQuery({ queryKey: ['analyzer-sessions'], queryFn: () => api.sessions.list({ page: 1, pageSize: 30 }) })
  const chosen = sessionId || sessions?.items[0]?.workSessionId || ''
  const { data: facts, isLoading } = useQuery({ queryKey: ['facts', chosen], queryFn: () => api.analysis.facts(chosen), enabled: Boolean(chosen) })

  const ask = useMutation({
    mutationFn: () => api.analysis.ask(chosen, question.trim()),
    onSuccess: (order) => {
      setOrderId(order.id)
      setProblem(null)
    },
    onError: (error) => setProblem(error instanceof ApiError && error.code === 'plan_limit' ? t('analyzer.proOnly') : error instanceof ApiError && error.code === 'remote_off' ? t('work.error.remote_off') : t('error.generic')),
  })
  const { order, finished } = useOrderResult(orderId)

  return (
    <div className="stack">
      <p className="small muted" style={{ margin: 0, maxWidth: 760 }}>{t('analyzer.intro')}</p>

      <label className="field" style={{ maxWidth: 520 }}>
        <span className="field__label">{t('analyzer.session')}</span>
        <select className="select" value={chosen} onChange={(e) => { setSessionId(e.target.value); setOrderId(null) }}>
          {(sessions?.items ?? []).map((s) => (
            <option key={s.workSessionId} value={s.workSessionId}>
              {new Date(s.startedAt).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' })} · {s.developerName} · {s.taskTitle ?? s.taskExternalId ?? t('analyzer.noTask')}
            </option>
          ))}
        </select>
      </label>

      {isLoading && <Skeleton height={160} />}
      {facts && (
        <section className="panel stack analyzer__facts">
          <strong className="small row row--tight"><HelpCircle size={14} /> {t('analyzer.why')}</strong>
          <ul className="small analyzer__findings">
            {facts.findings.map((finding, i) => <li key={i}>{finding}</li>)}
          </ul>
          <div className="row row--wrap tiny muted">
            <span>{t('analyzer.duration', { value: duration(facts.durationSeconds) })}</span>
            <span>{t('analyzer.idle', { value: duration(facts.idleSeconds) })}</span>
            <span>{t('analyzer.tests', { failed: facts.failedTestRuns, total: facts.testRuns })}</span>
            <span>{t('analyzer.tokens', { input: facts.inputTokens, cached: facts.cachedInputTokens, output: facts.outputTokens })}</span>
          </div>
          {facts.steps.length > 0 && (
            <div className="analyzer__steps">
              {facts.steps.map((step) => (
                <div key={step.title} className="analyzer__step" title={`${step.title}: ${duration(step.seconds)}`}>
                  <span className="tiny truncate">{step.title}</span>
                  <div className="analyzer__bar"><div style={{ width: `${Math.max(2, step.percent)}%` }} /></div>
                  <span className="tiny muted">{step.percent}%</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="panel stack">
        <strong className="small">{t('analyzer.askTitle')}</strong>
        <span className="tiny muted">{t('analyzer.askHint')}</span>
        <div className="row row--tight">
          <input className="input" style={{ flex: 1 }} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={t('analyzer.askPlaceholder')} maxLength={1000}
            onKeyDown={(e) => { if (e.key === 'Enter' && question.trim() && chosen) ask.mutate() }} />
          <button type="button" className="btn btn--primary" onClick={() => ask.mutate()} disabled={!question.trim() || !chosen || ask.isPending}>
            <Send size={14} /> {t('analyzer.ask')}
          </button>
        </div>
        {problem && <ErrorBanner message={problem} />}
        {problem === t('analyzer.proOnly') && <Link to="/settings/billing" className="small">{t('plans.upgrade')}</Link>}
        {orderId && !finished && <span className="small muted row row--tight"><Loader2 size={14} className="spin" /> {t('analyzer.waiting')}</span>}
        {finished && order?.lastReply && <pre className="runner-log small analyzer__answer">{order.lastReply.replace(/\n?DEVPULSE-STATUS:.*$/m, '')}</pre>}
      </section>
    </div>
  )
}
